/** @format */

import i18n, { normalizeLocale } from 'src/i18n';
import { CONTENT_LANGUAGES } from 'src/constants';

const MANUAL_LANGUAGE_KEY = 'manualLanguageSelection';

const readStoredLanguage = (): string => {
  if (typeof window === 'undefined') {
    return '';
  }

  try {
    return String(window.localStorage.getItem('i18nextLng') || '')
      .replace(/^["']|["']$/g, '')
      .trim();
  } catch {
    return '';
  }
};

const hasManualLanguageSelection = (): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  try {
    return window.localStorage.getItem(MANUAL_LANGUAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

export const clearManualLanguageSelection = (): void => {
  if (typeof window === 'undefined') {
    return;
  }

  try {
    window.localStorage.removeItem(MANUAL_LANGUAGE_KEY);
  } catch {
    // ignore storage errors
  }
};

export const setAppLanguage = async (
  locale?: string | null,
  { manual = false }: { manual?: boolean } = {}
): Promise<string> => {
  const lang = normalizeLocale(locale) || 'en';

  if (typeof window !== 'undefined') {
    try {
      if (manual) {
        window.localStorage.setItem(MANUAL_LANGUAGE_KEY, 'true');
      }
      window.localStorage.setItem('i18nextLng', lang);
    } catch {
      // ignore storage errors
    }
  }

  await i18n.changeLanguage(lang);

  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('lang', lang);
  }

  return lang;
};

export const applyLoginLocale = async (userLocale?: string | null): Promise<string> => {
  if (hasManualLanguageSelection()) {
    const lang = normalizeLocale(readStoredLanguage()) || getAppLanguage();
    return setAppLanguage(lang);
  }

  const lang = userLocale ? normalizeLocale(userLocale) : null;
  return setAppLanguage(lang || 'en');
};

export const getAppLanguage = (): string => {
  const fromI18n = normalizeLocale(
    (typeof i18n.resolvedLanguage === 'string' && i18n.resolvedLanguage) ||
      (typeof i18n.language === 'string' && i18n.language) ||
      ''
  );
  const fromStorage = normalizeLocale(readStoredLanguage());

  return fromStorage || fromI18n || 'en';
};

const mapToContentLanguage = (raw?: string | null): string => {
  if (!raw) {
    return 'en';
  }

  const lowerRaw = String(raw).toLowerCase();
  const exact = CONTENT_LANGUAGES.find(language => language.value.toLowerCase() === lowerRaw);
  if (exact) {
    return exact.value;
  }

  const base = lowerRaw.split('-')[0];
  const byBase = CONTENT_LANGUAGES.find(language => language.value.toLowerCase() === base);
  if (byBase) {
    return byBase.value;
  }

  return normalizeLocale(raw) || 'en';
};

export const resolveSelectedLanguage = (): string => mapToContentLanguage(getAppLanguage());

export const getContentLanguage = (explicitLanguage?: string | null): string => {
  if (typeof explicitLanguage === 'string' && explicitLanguage.trim()) {
    const trimmed = explicitLanguage.trim().split('|')[0].trim();
    return normalizeLocale(trimmed) || trimmed;
  }

  return getAppLanguage();
};
